import * as Promise from 'bluebird';

/**
 * Take an async request function and return a queue that serializes calls to that request
 * function. At any given time, the queue may be in one of three states: 1) Empty 2) 1 request
 * in flight, or 3) 1 request in flight and 1 request pending.
 * @param requestFn A function that performs a single request
 */
export class RequestQueue<T> {
    private request : Promise<T | undefined>;
    private requestQueued : boolean;

    constructor(private readonly requestFn : (this : RequestQueue<T>) => PromiseLike<T>) {
        this.request = Promise.resolve(undefined);
        this.requestQueued = false;
    }

    /**
     * Enqueue a request, provided none is already pending.
     * If the queue is empty, enqueues a new request to run on the next tick.
     * If a request is in flight, enqueues a new request to run when that in-flight request completes (is resolved or rejected).
     * If a request is already pending after the in-flight request, this call is a no-op.
     */
    enqueueRequest() {
        if (this.requestQueued) return;

        this.requestQueued = true;
        const fireNext = () => {
            this.requestQueued = false;
            return this.requestFn();
        };
        // .then(fireNext, fireNext) instead of .finally(fireNext) so that the resolution/rejection status
        // of the new Promise depends only on the most recent call to requestFn. Otherwise one failed request
        // would cause all later Promises constructed to count as rejected.
        // Matters for consumers that use the mostRecentRequest method and care about the resolution/rejection status.
        this.request = this.request.then(fireNext, fireNext);
    }

    /**
     * Return the most recent request in the request queue, or a Promise resolved to undefined if no request has ever been issued.
     * The most recent request means:
     * - The last request completed if no request is pending or in flight, or a Promise resolved to undefined if no request has ever been issued.
     * - The in-flight request, if a request is in flight and no request is pending after that.
     * - The pending request, if a request is in flight and a request is pending after that.
     */
    mostRecentRequest() {
        return this.request;
    }
}



// WEBPACK FOOTER //
// ./lib/requestQueue.ts